/**
 * Tenant capability registry.
 *
 * Single source of truth for every permanent product capability that can be
 * enabled or disabled per tenant. Add a new capability here, then update
 * `src/server/derive.ts` to define when it applies.
 *
 * - Public capabilities (visible in rendered UI anyway) are safe to ship to
 *   the client via the CapabilitiesProvider.
 * - Sensitive capabilities (unannounced betas, internal-only flags) should be
 *   evaluated server-side only and not added to the public list.
 *
 * Only the registry (keys + descriptions) is exported here so client code
 * can reference capability keys by name. The rules that map tenant
 * attributes to enabled capabilities live in `@kit/capabilities/server`
 * and never reach the client bundle.
 */

export const Capabilities = {
  ONLINE_PAYMENT_REPORTS: 'online_payment_reports',
  /** Family member management: add/manage children/dependents on a single account. */
  FAMILIES: 'families',
  /**
   * Unified class filter: collapses the booking-type pills into a single
   * "All Classes" + per-template-pill row, suitable for tenants whose entire
   * booking flow is class-based (e.g. studios with no court/PT counterpart).
   */
  UNIFIED_CLASS_FILTER: 'unified_class_filter',
  /** Members can invite guests via per-visit guest passes (Phase 1 member-club). */
  GUEST_PASSES: 'guest_passes',
  /** Member QR (rotating, TOTP-style) + gate scan flow (Phase 1 member-club). */
  MEMBER_QR_CHECKIN: 'member_qr_checkin',
  /** Staff blacklist admin (Phase 1 member-club). */
  BLACKLIST: 'blacklist',
  /** Staff settings for guest caps, blackouts, and per-tier guest policies (Phase 1 member-club). */
  GUEST_RULES_ADMIN: 'guest_rules_admin',
  /**
   * Front desk is locked to the /checkin gate console (member clubs). Tenants
   * with check-in but without this (gyms) keep the front desk's booking screens.
   */
  FRONT_DESK_CHECKIN_ONLY: 'front_desk_checkin_only',
  /** Entitlement bundles (co-working, classes, spa, monthly resets) — Phase 2 placeholder, not yet enabled. */
  ENTITLEMENTS: 'entitlements',
  /** Tour booking flow after intake — Phase 2 placeholder, not yet enabled. */
  TOUR_BOOKING: 'tour_booking',
  /**
   * Dynamic slot mode toggle on a sport/discipline. Studios run fixed
   * class schedules, so 30-minute rolling slots with automatic resource
   * assignment don't apply — the toggle is hidden for them.
   */
  DYNAMIC_SLOT_MODE: 'dynamic_slot_mode',
  /**
   * Peak vs off-peak pricing. A court-rental concept (peak HOURS cost more).
   * Classes charge normal_price only — peak_price is never read by any class
   * pricing path — so studios get a single-price form instead.
   */
  PEAK_PRICING: 'peak_pricing',
  /**
   * Bookable EQUIPMENT as a resource (ball machines and the like). A court
   * concept: studios staff classes with instructors and have no equipment a
   * customer books by the hour, so the resource-type picker is noise there.
   */
  EQUIPMENT_RESOURCES: 'equipment_resources',
  /** Reports module: revenue, payment-method, peak/off-peak, top services. Enabled for everyone with bookings. */
  REVENUE_REPORTS: 'revenue_reports',
  /** Reports: court occupancy, utilization %, day×hour heatmap. Hidden when tenant has no courts (studios, restaurants). */
  COURT_REPORTS: 'court_reports',
  /** Reports: coach × product revenue, coaching hours, top coaches. */
  COACHING_REPORTS: 'coaching_reports',
  /** Reports: class enrollments, attendance, per-class revenue. */
  CLASS_REPORTS: 'class_reports',
  /** Reports: packages sold, credits, expiring soon, sport breakdown. */
  PACKAGE_REPORTS: 'package_reports',
  /** Reports: clients new/active/inactive, retention cohorts, top clients. */
  CLIENT_REPORTS: 'client_reports',
  /** Reports: add-on and discount performance, attach rate. */
  ADDON_REPORTS: 'addon_reports',
  /** Reports: memberships sold, active, expiring, churn, plan breakdown. */
  MEMBERSHIP_REPORTS: 'membership_reports',
  /**
   * Private court rental: courts can be booked directly by customers via
   * slot (pricing) templates. When disabled, the court admin surface hides
   * the rental template assignment — courts act as class-only spaces.
   */
  PRIVATE_COURT_RENTAL: 'private_court_rental',
  /**
   * Per-plan advance-booking horizons. When enabled, the membership-plan
   * admin drawer exposes two independent fields: `advance_booking_days`
   * (ad-hoc court/coaching booking on /book, resolved by
   * `get_effective_advance_booking_days`, overrides the venue-wide horizon)
   * and `fixed_slot_advance_booking_days` (the fixed-slot package purchase
   * grid on /book/packages, resolved by
   * `get_fixed_slot_grid_advance_booking_days`, widens the 7-day floor). Intended for
   * tenants that sell tiered access (e.g. padel courts with VIP windows,
   * member clubs with priority booking).
   */
  MEMBERSHIP_ADVANCE_BOOKING_DAYS: 'membership_advance_booking_days',
  /**
   * Booking export offers Club Code / Club Name columns (the booking's
   * court_location slug and name) for finance/PM reporting. Schools tenants
   * call their facilities "clubs"; other business types keep the export
   * unchanged.
   */
  CLUB_EXPORT_COLUMNS: 'club_export_columns',
  /**
   * Calendar class events are colored per court_location instead of the
   * single orange "class" color. Only kicks in when the tenant actually has
   * 2+ locations (the calendar checks that at render time). Built for
   * studios, whose calendar is all classes — one orange wall carries no
   * information, while per-location hues let staff scan a mixed-location
   * week at a glance.
   */
  LOCATION_CLASS_COLORS: 'location_class_colors',
  /**
   * Service options: a pricing template can be sold as one option of another
   * template ("Part of service"), so customers see one service name with a
   * choice of options and prices. Rolled out to member clubs first — their
   * catalogue is a small set of services each sold in several shapes. Other
   * business types keep a flat template list until the model is proven.
   *
   * Gates the admin AUTHORING surfaces only (the "Part of service" picker in
   * the template drawer, and the multi-select "Group selected" flow). Already-
   * grouped templates keep rendering their grouping everywhere, so a tenant
   * that is switched away from member_club never sees its catalogue silently
   * flatten.
   */
  SERVICE_OPTIONS: 'service_options',
  /**
   * Staff may record a cash-paid transaction from the BOOKING CALENDAR surface
   * (payment dialog, participant add-ons, split payers, enterprise invoice
   * payments, class participants).
   *
   * SCOPE LIMIT: memberships, packages, and POS also record cash and are NOT
   * gated by this capability yet — they still accept cash for every business
   * type. Extend those actions before relying on this as a tenant-wide
   * "no cash anywhere" switch.
   *
   * Distinct from `business_settings.enable_cash_payment`, which governs only
   * whether CUSTOMERS see "Pay at venue" on the public /book flow. The two are
   * independent: a venue can decline cash online while its front desk still
   * takes cash daily, which is why this is not folded into that column.
   */
  STAFF_CASH_COLLECTION: 'staff_cash_collection',
} as const;

export type Capability = (typeof Capabilities)[keyof typeof Capabilities];

/**
 * - entitlement: what the tenant's plan or contract includes.
 * - preference: the tenant asked to hide or show something.
 * - release: temporary rollout gate; must carry `expiresAt` and be deleted after launch.
 */
export type CapabilityOverrideKind = 'entitlement' | 'preference' | 'release';

interface CapabilityOverrideDefinition {
  kind: CapabilityOverrideKind;
  owner: string;
  expiresAt?: string;
}

/**
 * Capabilities a super admin may override per tenant in
 * `public.tenant_capability_overrides`. The resolver ignores rows whose key is
 * not listed here, so retiring a key is a one-line change. Overrides only
 * replace the business-type default; they never bypass staff permissions or
 * payment-provider limits.
 */
export const OVERRIDABLE_CAPABILITIES = {
  [Capabilities.ONLINE_PAYMENT_REPORTS]: { kind: 'preference', owner: 'payments' },
} as const satisfies Partial<Record<Capability, CapabilityOverrideDefinition>>;

export type OverridableCapability = keyof typeof OVERRIDABLE_CAPABILITIES;

export const OVERRIDABLE_CAPABILITY_KEYS = Object.keys(OVERRIDABLE_CAPABILITIES) as OverridableCapability[];

export function isOverridableCapability(key: string): key is OverridableCapability {
  return Object.hasOwn(OVERRIDABLE_CAPABILITIES, key);
}

export const CAPABILITY_DESCRIPTIONS: Record<Capability, string> = {
  online_payment_reports: 'Online Payments and Online Settlements admin pages. Does not control payment processing.',
  families: 'Family members and dependent profiles attached to a single account.',
  unified_class_filter:
    'Class booking flow shows an "All" pill plus per-template pills, with no booking-type axis.',
  guest_passes: 'Members can invite guests via per-visit guest passes.',
  member_qr_checkin: 'Member QR (rotating) and staff gate scan flow.',
  blacklist: 'Staff blacklist admin.',
  guest_rules_admin: 'Staff settings for guest caps, blackouts, and per-tier guest policies.',
  front_desk_checkin_only:
    'Front desk staff are limited to the /checkin gate console. Member clubs only; gyms with check-in keep the full front-desk surface.',
  entitlements: 'Entitlement bundles (co-working, classes, spa) with monthly resets.',
  tour_booking: 'Tour booking flow after membership intake.',
  revenue_reports: 'Revenue reports tab: totals, payment-method split, peak/off-peak, top services.',
  court_reports: 'Occupancy reports tab: utilization %, day×hour heatmap, per-court breakdowns.',
  coaching_reports: 'Coaching reports tab: coach × product revenue, coaching hours, top coaches.',
  class_reports: 'Class reports tab: enrollments, attendance, per-class revenue.',
  package_reports: 'Package reports tab: sold/credits/expiring, sport breakdown.',
  client_reports: 'Client reports tab: new/active/inactive, retention cohorts, top clients.',
  addon_reports: 'Add-on reports tab: gross/discount/net, attach rate, top upsells.',
  membership_reports: 'Membership reports tab: active members, new signups, plan revenue, expiring soon.',
  private_court_rental:
    'Courts are directly rentable by customers via slot/pricing templates; hides the rental template assignment in court admin when disabled.',
  membership_advance_booking_days:
    'Per-plan advance-booking horizons (admin drawer fields) — one overrides the venue-wide court booking horizon for members of that plan, the other widens the 7-day fixed-slot package grid.',
  club_export_columns:
    'Booking export offers Club Code / Club Name columns (facility slug + name) for schools tenants.',
  location_class_colors:
    'Calendar class events are colored per location (multi-location studios) instead of the single orange class color.',
  equipment_resources:
    'Resources can be EQUIPMENT (ball machines) as well as people, so the add-resource form offers a type picker. Off for studios, whose resources are always instructors.',
  peak_pricing:
    'Pricing forms expose a separate peak price alongside the normal price. Off for studios, whose classes charge one price regardless of hour.',
  dynamic_slot_mode:
    'Sport/discipline admin exposes the Dynamic Slot Mode toggle (30-minute rolling slots with automatic resource assignment). Hidden for studios, whose bookings are fixed-schedule classes.',
  service_options:
    'Admin can group pricing templates into one service with several options ("Part of service" picker + Group selected flow). Member clubs only; existing groupings still render for every business type.',
  staff_cash_collection:
    'Staff can record cash-paid transactions from the booking calendar (court bookings, class participants, add-ons, split payers, enterprise invoices). Does NOT yet gate memberships, packages, or POS — those surfaces still accept cash for every business type.',
};

/** All publicly-visible capability keys. Sensitive ones must not be listed here. */
export const PUBLIC_CAPABILITIES: readonly Capability[] = [
  Capabilities.FAMILIES,
  Capabilities.UNIFIED_CLASS_FILTER,
  Capabilities.GUEST_PASSES,
  Capabilities.MEMBER_QR_CHECKIN,
  Capabilities.BLACKLIST,
  Capabilities.GUEST_RULES_ADMIN,
  Capabilities.FRONT_DESK_CHECKIN_ONLY,
  Capabilities.ENTITLEMENTS,
  Capabilities.TOUR_BOOKING,
  Capabilities.REVENUE_REPORTS,
  Capabilities.COURT_REPORTS,
  Capabilities.COACHING_REPORTS,
  Capabilities.CLASS_REPORTS,
  Capabilities.PACKAGE_REPORTS,
  Capabilities.CLIENT_REPORTS,
  Capabilities.ADDON_REPORTS,
  Capabilities.MEMBERSHIP_REPORTS,
  Capabilities.DYNAMIC_SLOT_MODE,
  Capabilities.PEAK_PRICING,
  Capabilities.EQUIPMENT_RESOURCES,
  Capabilities.PRIVATE_COURT_RENTAL,
  Capabilities.MEMBERSHIP_ADVANCE_BOOKING_DAYS,
  Capabilities.CLUB_EXPORT_COLUMNS,
  Capabilities.LOCATION_CLASS_COLORS,
  Capabilities.STAFF_CASH_COLLECTION,
  Capabilities.SERVICE_OPTIONS,
];
